import { useState, useCallback, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes,
  faMapMarkerAlt,
  faRoute,
  faSearch,
  faLocationDot,
} from "@fortawesome/pro-regular-svg-icons";

import {
  useAppDispatch,
  useAppSelector,
  routeEndpointsSelector,
  customRouteEndSelector,
  customRouteFromWaypointSelector,
  fromWaypointNodeSelector,
  setCustomRouteEnd,
  setShowCustomRoute,
  clearCustomRoute,
  dismissWaypointPicker,
  waypointRouteResultSelector,
  waypointPickerDismissedSelector,
} from "../../../store";

import "./WaypointDestinationPicker.scss";

export function WaypointDestinationPicker() {
  const dispatch = useAppDispatch();
  const [searchTerm, setSearchTerm] = useState("");

  const fromWaypointId = useAppSelector(customRouteFromWaypointSelector);
  const fromWaypointNode = useAppSelector(fromWaypointNodeSelector);
  const endSpaceId = useAppSelector(customRouteEndSelector);
  const availableSpaces = useAppSelector(routeEndpointsSelector);
  const routeResult = useAppSelector(waypointRouteResultSelector);
  const pickerDismissed = useAppSelector(waypointPickerDismissedSelector);

  const filteredSpaces = useMemo(() => {
    if (!searchTerm.trim()) return availableSpaces;
    const term = searchTerm.toLowerCase();
    return availableSpaces.filter((space) => space.name.toLowerCase().includes(term));
  }, [availableSpaces, searchTerm]);

  const handleSelectDestination = useCallback((spaceId: string) => {
    dispatch(setCustomRouteEnd(spaceId));
    dispatch(setShowCustomRoute(true));
  }, [dispatch]);

  const handleClose = useCallback(() => {
    dispatch(clearCustomRoute());
  }, [dispatch]);

  const handleClearDestination = useCallback(() => {
    dispatch(setCustomRouteEnd(null));
    dispatch(setShowCustomRoute(false));
  }, [dispatch]);

  const handleDone = useCallback(() => {
    dispatch(dismissWaypointPicker());
  }, [dispatch]);

  if (!fromWaypointId || pickerDismissed) return null;

  if (!fromWaypointNode) {
    return (
      <div className="waypoint-destination-picker-overlay">
        <div className="waypoint-destination-picker">
          <header className="picker-header">
            <h2>Invalid Location</h2>
            <button className="close-btn" onClick={handleClose} title="Close">
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </header>
          <div className="picker-content">
            <p className="error-message">This QR code links to a location that no longer exists.</p>
            <button className="dismiss-btn" onClick={handleClose}>Dismiss</button>
          </div>
        </div>
      </div>
    );
  }

  const destinationName = availableSpaces.find((s) => s.id === endSpaceId)?.name;
  const hasDestination = !!(endSpaceId);

  return (
    <div className="waypoint-destination-picker-overlay">
      <div className="waypoint-destination-picker">
        <header className="picker-header">
          <h2>
            <FontAwesomeIcon icon={faRoute} />
            Where to?
          </h2>
          <button className="close-btn" onClick={handleClose} title="Close">
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </header>

        <div className="picker-content">
          <div className="current-location">
            <FontAwesomeIcon icon={faLocationDot} className="location-icon" />
            <div className="location-text">
              <span className="label">You are at</span>
              <span className="name">{fromWaypointNode.name || "Current Location"}</span>
            </div>
          </div>

          {!hasDestination ? (
            <>
              <div className="search-input">
                <FontAwesomeIcon icon={faSearch} className="search-icon" />
                <input
                  type="text"
                  placeholder="Search destinations..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  autoFocus
                />
              </div>
              <div className="destination-list">
                {filteredSpaces.map((space) => (
                  <button key={space.id} className="destination-item" onClick={() => handleSelectDestination(space.id)}>
                    <FontAwesomeIcon icon={faMapMarkerAlt} className="marker-icon" />
                    <span className="space-name">{space.name}</span>
                  </button>
                ))}
                {filteredSpaces.length === 0 && (
                  <p className="no-results">
                    {searchTerm ? "No matching destinations found." : "No destinations available."}
                  </p>
                )}
              </div>
            </>
          ) : (
            <div className="route-result">
              {routeResult?.found ? (
                <>
                  <div className="destination-selected">
                    <FontAwesomeIcon icon={faMapMarkerAlt} className="end-icon" />
                    <span>Going to: <strong>{destinationName}</strong></span>
                  </div>
                  <div className="route-actions">
                    <button className="change-btn" onClick={handleClearDestination}>Change Destination</button>
                    <button className="close-route-btn" onClick={handleDone}>Done</button>
                  </div>
                </>
              ) : (
                <div className="no-route">
                  <p>Unable to find a route to this destination.</p>
                  <button className="change-btn" onClick={handleClearDestination}>Choose Another</button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
